/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import type { Token } from "marked";
import type { Content, TDocumentDefinitions } from "pdfmake/interfaces";
import { AppInfo } from "Api";
import { docSections, DocSection } from "../DocSections";
import {
  DEXTER_DESCRIPTION,
  DEXTER_DOCUMENT_TITLE,
  DEXTER_TITLE,
  DATA_RIGHTS_LEGEND,
} from "../DocumentationMeta";
import { fetchAsDataUrl, resolveImages } from "./PdfAssets";
import {
  collectImageSources,
  ImageMap,
  lexMarkdown,
  renderBlockTokens,
} from "./MarkdownToPdf";
import { BODY_FONT, COLORS, PAGE_MARGIN, pdfStyles } from "./PdfStyles";

/**
 * Builds and downloads a PDF of the Reference section.
 *
 * The document is generated from the same `docSections` the Reference page
 * renders, so edits to the markdown are reflected in the PDF with no separate
 * build step.
 */

export interface DocumentationPdfOptions {
  /** Logo shown on the cover page. Must resolve to a PNG or JPEG. */
  logoUrl?: string;
  /** Version metadata for the cover page. The cover omits it when absent. */
  appInfo?: AppInfo;
}

export interface DocumentationPdfResult {
  /** Screenshots that could not be fetched and were therefore omitted. */
  readonly missingImages: readonly string[];
}

interface LexedSection {
  section: DocSection;
  tokens: Token[];
}

interface PdfMakeStatic {
  addVirtualFileSystem: (vfs: unknown) => void;
  createPdf: (definition: TDocumentDefinitions) => {
    download: (fileName: string) => PromiseLike<void> | void;
  };
}

/**
 * pdfmake and its bundled fonts are well over a megabyte, so they are imported
 * dynamically. Parcel emits them as a separate chunk that is only downloaded
 * when a user actually asks for the PDF.
 */
let pdfMakeLoad: Promise<PdfMakeStatic> | undefined;

const loadPdfMake = (): Promise<PdfMakeStatic> => {
  pdfMakeLoad =
    pdfMakeLoad ??
    (async (): Promise<PdfMakeStatic> => {
      const [pdfMakeModule, vfsModule] = await Promise.all([
        import("pdfmake/build/pdfmake"),
        import("pdfmake/build/vfs_fonts"),
      ]);

      const pdfMake = (pdfMakeModule.default ??
        pdfMakeModule) as unknown as PdfMakeStatic;
      const pdfFonts = vfsModule.default ?? vfsModule;

      if (typeof pdfMake.addVirtualFileSystem !== "function") {
        throw new Error(
          "pdfmake did not expose addVirtualFileSystem; the installed version " +
            "does not match what this module expects."
        );
      }
      pdfMake.addVirtualFileSystem(pdfFonts);

      return pdfMake;
    })();

  return pdfMakeLoad;
};

export const generateDocumentationPdf = async (
  options: DocumentationPdfOptions = {}
): Promise<DocumentationPdfResult> => {
  const lexed: LexedSection[] = docSections.map((section) => ({
    section,
    tokens: lexMarkdown(section.content),
  }));

  const imageSources = Array.from(
    new Set(lexed.flatMap(({ tokens }) => collectImageSources(tokens)))
  );

  const [{ images, failed }, logoDataUrl, pdfMake] = await Promise.all([
    resolveImages(imageSources),
    loadLogo(options.logoUrl),
    loadPdfMake(),
  ]);

  const definition = buildDocDefinition(lexed, images, {
    logoDataUrl,
    appInfo: options.appInfo,
  });

  const download: unknown = pdfMake
    .createPdf(definition)
    .download(fileNameFor(options.appInfo));

  // Duck-typed rather than `instanceof Promise`: pdfmake's browser build ships
  // its own polyfills, so the promise it returns is not guaranteed to share a
  // constructor with the one this module sees.
  if (typeof (download as PromiseLike<void> | null)?.then === "function") {
    await (download as PromiseLike<void>);
  }

  return { missingImages: failed };
};

/** A missing logo should not prevent the document from being produced. */
const loadLogo = async (logoUrl?: string): Promise<string | undefined> => {
  if (!logoUrl) {
    return undefined;
  }
  try {
    return await fetchAsDataUrl(logoUrl);
  } catch {
    return undefined;
  }
};

const fileNameFor = (appInfo?: AppInfo): string => {
  const descriptor = appInfo?.version.descriptor;
  if (!descriptor) {
    return "Dexter-Documentation.pdf";
  }
  const safeDescriptor = descriptor.replace(/[^\w.-]+/g, "-");
  return `Dexter-Documentation-${safeDescriptor}.pdf`;
};

/* -------------------------------------------------------------------------- */
/* Document assembly                                                           */
/* -------------------------------------------------------------------------- */

interface CoverOptions {
  logoDataUrl?: string;
  appInfo?: AppInfo;
}

const buildDocDefinition = (
  lexed: readonly LexedSection[],
  images: ImageMap,
  cover: CoverOptions
): TDocumentDefinitions => ({
  info: {
    title: `${DEXTER_TITLE} ${DEXTER_DOCUMENT_TITLE}`,
  },
  pageSize: "LETTER",
  pageMargins: [PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN],
  defaultStyle: {
    font: BODY_FONT,
    fontSize: 10.5,
    color: COLORS.text,
  },
  styles: pdfStyles,
  footer: renderFooter,
  content: [
    ...buildCoverPage(cover),
    { toc: { title: { text: "Contents", style: "tocTitle" } } },
    ...lexed.flatMap(({ section, tokens }) =>
      buildSection(section, tokens, images)
    ),
  ],
});

const buildCoverPage = ({ logoDataUrl, appInfo }: CoverOptions): Content[] => {
  const version = appInfo?.version;

  const versionLines: Content[] = version
    ? [{ text: version.descriptor, style: "coverMeta" }]
    : [];

  const logo: Content[] = logoDataUrl
    ? [
        {
          image: logoDataUrl,
          fit: [240, 96],
          alignment: "center",
          margin: [0, 72, 0, 36],
        },
      ]
    : [];

  return [
    ...logo,
    { text: DEXTER_TITLE, style: "coverTitle" },
    { text: DEXTER_DOCUMENT_TITLE, style: "coverSubtitle" },
    { text: DEXTER_DESCRIPTION, style: "coverDescription" },
    ...versionLines,
    {
      text: `Generated ${new Date().toLocaleDateString()}`,
      style: "coverMeta",
      margin: [0, 12, 0, 0],
    },
    { text: DATA_RIGHTS_LEGEND, style: "coverLegend" },
    // Everything after the cover starts on a fresh page.
    { text: "", pageBreak: "after" },
  ];
};

const buildSection = (
  section: DocSection,
  tokens: Token[],
  images: ImageMap
): Content[] => [
  {
    text: section.title,
    style: "h1",
    tocItem: true,
    pageBreak: "before",
  },
  ...renderBlockTokens(tokens, images),
];

/** The cover page carries no footer. */
const renderFooter = (currentPage: number, pageCount: number): Content => {
  if (currentPage === 1) {
    return { text: "" };
  }

  return {
    columns: [
      { text: `${DEXTER_TITLE} ${DEXTER_DOCUMENT_TITLE}`, style: "footer" },
      {
        text: `Page ${currentPage} of ${pageCount}`,
        style: "footer",
        alignment: "right",
      },
    ],
    margin: [PAGE_MARGIN, 16, PAGE_MARGIN, 0],
  };
};
